/**
 * Bounded backoff schedule for re-attempting auth after the backend was UNREACHABLE.
 *
 * Kept dependency-free (pure schedule + a module-scoped counter) so it is unit-testable
 * in the node jest env, and so the counter survives the remount that happens every time
 * a retry flips the auth status through 'autoJwtStarted' (busy) and back — component
 * state would reset to 0 each cycle and retry forever.
 */

/** Delay before retry #1, #2, #3. Length == the retry budget. */
export const AUTH_RETRY_DELAYS_MS: readonly number[] = [1500, 4000, 8000];

/** Delay before the next retry, or null once the budget is spent. */
export const nextAuthRetryDelayMs = (attemptsSoFar: number): number | null => {
  if (!Number.isFinite(attemptsSoFar) || attemptsSoFar < 0) return AUTH_RETRY_DELAYS_MS[0];
  return attemptsSoFar < AUTH_RETRY_DELAYS_MS.length ? AUTH_RETRY_DELAYS_MS[attemptsSoFar] : null;
};

/** True once the retry budget is spent (show the "can't reach the server" state). */
export const isAuthRetryBudgetSpent = (attemptsSoFar: number): boolean =>
  nextAuthRetryDelayMs(attemptsSoFar) === null;

let attempts = 0;

export const getAuthRetryAttempts = (): number => attempts;
export const recordAuthRetryAttempt = (): number => (attempts += 1);
/** Called on a successful auth and on an explicit "Try again". */
export const resetAuthRetryAttempts = (): void => { attempts = 0; };
